/**
 * Zone + Commission DTOs exposed by operator-service `/api/zones` and
 * `/api/admin/commission-*`.
 */

export type ZoneType = 'CITY' | 'REGION' | 'COUNTRY' | 'GLOBAL';
export type CommissionChangeScope = 'GLOBAL' | 'OPERATOR_OVERRIDE';

export interface Zone {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  // ── domain ────────────────────────────────────────────────────────────
  code: string;
  name: string;
  zoneType: ZoneType;
  /** GeoJSON polygon — `null` until set via the dedicated endpoint. */
  polygon?: string | null;
}

/** Body of `POST /api/zones`. */
export interface AdminCreateZoneRequest {
  code: string;
  name: string;
  zoneType: ZoneType;
}

/** Body of `PATCH /api/zones/{code}`. All fields optional. */
export interface AdminUpdateZoneRequest {
  name?: string | null;
  zoneType?: ZoneType | null;
}

/** Body of `PUT /api/zones/{code}/polygon`. */
export interface SetZonePolygonRequest {
  geoJsonPolygon: string;
}

// ── Commission ─────────────────────────────────────────────────────────

export interface CommissionConfig {
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  defaultTotalPct: number;
  defaultPlatformPct: number;
  defaultOperatorPct: number;
}

/** Body of `PUT /api/admin/commission-config`. */
export interface CommissionConfigRequest {
  defaultTotalPct: number;
  defaultPlatformPct: number;
  defaultOperatorPct: number;
}

/** Body of `PUT /api/admin/operators/{id}/commission-override`. All nullable.
 *  Passing all-null removes the override (fall back to global). */
export interface CommissionOverrideRequest {
  customTotalPct: number | null;
  customPlatformPct: number | null;
  customOperatorPct: number | null;
}

export interface CommissionConfigHistory {
  id: number;
  createdAt?: string | null;
  scope: CommissionChangeScope;
  targetOperatorId?: number | null;
  adminUserId?: number | null;
  oldTotalPct?: number | null;
  oldPlatformPct?: number | null;
  oldOperatorPct?: number | null;
  newTotalPct?: number | null;
  newPlatformPct?: number | null;
  newOperatorPct?: number | null;
  reason?: string | null;
}

// ── UI helpers ─────────────────────────────────────────────────────────

export const ZONE_TYPE_LABEL: Record<ZoneType, string> = {
  CITY: 'Ville',
  REGION: 'Région',
  COUNTRY: 'Pays',
  GLOBAL: 'Global',
};

export const ZONE_TYPE_OPTIONS: Array<{ value: ZoneType; label: string }> = [
  { value: 'CITY', label: 'Ville' },
  { value: 'REGION', label: 'Région' },
  { value: 'COUNTRY', label: 'Pays' },
  { value: 'GLOBAL', label: 'Global' },
];

export const COMMISSION_SCOPE_LABEL: Record<CommissionChangeScope, string> = {
  GLOBAL: 'Taux global',
  OPERATOR_OVERRIDE: 'Override opérateur',
};
